import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import "./KnowYourEligibilityForm.css";
import { Eye, ExternalLink, Minus, Plus, Search, SlidersHorizontal } from "lucide-react";
import SchemeAndGRDetailsModal from "../modals/SchemeAndGRDetailsModal.jsx";
import GRDownloadModal from "../modals/GRDownloadModal.jsx";
import ApplicationSubmissionModal from "../modals/ApplicationSubmissionModal.jsx";
import {
  fetchCasteCategoryOptions,
  fetchCasteOptions,
  checkEligibilityAgainstSchemeConfigurators,
  fetchCoursesForCollegeId,
  fetchGenderOptions,
  fetchReligionOptions,
  fetchBeneficiaryCategoryOptions,
  fetchStateOptions,
  fetchDistrictOptionsByState,
  fetchTalukaOptionsByDistrict,
  fetchInstitutionOptions,
  fetchAcademicYearOptions,
  checkUserAlreadyAppliedForScheme,
  validateSchemeEligibilityFromSchemeData,
  fetchSchemeById,
  fetchCollegeById,
  fetchApiDataBaseOnSchemeIdAndMahaId,
  fetchJobTitle,
  fetchUserAadhaarProfileByEmail,
  fetchBrowsableSchemeConfigurators,
  fetchDepartmentsOptions,
  fetchQualificationLevelOptions,
  fetchBirthOrderOptions,
  fetchIsProfessionalFromCollegeAndCourse,
} from "../api/masterData.js";
import {
  fetchSchemesBySchemeId,
  checkUserAlreadyApplied,
  getUserRolesById,
} from "../api/fetch-schemes";

const schemeTypeOptions = [
  { value: "postMatric", label: "College Student (Post-Matric)" },
  { value: "preMatric", label: "School Student (Pre-Matric)" },
  // { value: "farmerSchemes", label: "Farmer (Agriculture)" },
  { value: "pensionSchemes", label: "Pensioner (SAS)" },
  { value: "specialAssistanceSchemes", label: "Other (Assistance)" },
];

const headMasterSchemeTypeOptions = [
  { value: "preMatric", label: "School Student (Pre-Matric)" },
];

const initialForm = {
  schemeType: [],
  domicileOfMaharashtra: "",
  religion: "",
  casteCategory: "",
  casteCategoryLabel: "",
  beneficiaryCategory: "",
  caste: "",
  gender: "",
  disability: "",
  familyAnnualIncome: "",
  alpaBhuOrRegisteredLabour: "",
  hazardousOrUncleanOccupation: "",
  birthOrder: "",
  birthOrderLabel: "",
  grade10CompletedFromMaharashtra: "",
  admissionThroughCET: "",
  completed10thFromGovernmentVidyaniketan: "",
  stateId: "",
  stateLabel: "",
  districtId: "",
  districtLabel: "",
  talukaId: "",
  talukaLabel: "",
  universityId: "",
  institutionName: "",
  universityName: "",
  currentCourseName: "",
  currentCourseNameLabel: "",
  courseType: "",
  courseTypeLabel: "",
  yearOfStudyOrCourseLevel: "",
  yearOfStudyOrCourseLevelLabel: "",
  udiseCodeOfSchool: "",
};

const NON_MEANINGFUL_FILTER_KEYS = new Set([
  "schemeType",
  "casteCategoryLabel",
  "birthOrderLabel",
  "stateLabel",
  "districtLabel",
  "talukaLabel",
  "institutionName",
  "universityName",
  "currentCourseNameLabel",
  "courseTypeLabel",
  "yearOfStudyOrCourseLevelLabel",
]);

const courseTypeOptions = [
  { value: "professional", label: "Professional" },
  { value: "nonProfessional", label: "Non Professional" },
];

const yesNoOptions = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

const preMatricStandardOptions = [
  { value: "1st", label: "1st" },
  { value: "2nd", label: "2nd" },
  { value: "3rd", label: "3rd" },
  { value: "4th", label: "4th" },
  { value: "5th", label: "5th" },
  { value: "6th", label: "6th" },
  { value: "7th", label: "7th" },
  { value: "8th", label: "8th" },
  { value: "9th", label: "9th" },
  { value: "10th", label: "10th" },
];

const FAMILY_INCOME_MAX = 1200000;
const FAMILY_INCOME_MAX_LABEL_VALUE = 1200000;
const FAMILY_INCOME_STEP = 10000;

function formatIncomeSliderLabel(value) {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return "0 L";
  }

  if (numericValue >= FAMILY_INCOME_MAX_LABEL_VALUE) {
    return "12 L";
  }

  return `${(numericValue / 100000).toFixed(1).replace(/\.0$/, "")} L`;
}


function getSchemeBenefitsHtmlContent(scheme) {
  const rawScheme = scheme?.raw || scheme || {};
  const candidates = [
    rawScheme?.schemeBenefits,
    rawScheme?.schemebenefits,
    rawScheme?.schemeBenefit,
    rawScheme?.schemebenefit,
    rawScheme?.benefitsHtml,
    rawScheme?.benefitshtml,
  ];

  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (value) {
      return value;
    }
  }

  return "";
}

/** Windowed page list with ellipsis markers, e.g. [1, "left-ellipsis", 4, 5, 6, "right-ellipsis", 16]. */
function getPaginationPageNumbers(currentPage, totalPages, siblingCount = 1) {
  const totalVisibleNumbers = siblingCount * 2 + 5;

  if (totalPages <= totalVisibleNumbers) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);
  const showLeftEllipsis = leftSiblingIndex > 2;
  const showRightEllipsis = rightSiblingIndex < totalPages - 1;

  const pages = [1];

  if (showLeftEllipsis) {
    pages.push("left-ellipsis");
  } else {
    for (let page = 2; page < leftSiblingIndex; page += 1) {
      pages.push(page);
    }
  }

  for (let page = leftSiblingIndex; page <= rightSiblingIndex; page += 1) {
    if (page !== 1 && page !== totalPages) {
      pages.push(page);
    }
  }

  if (showRightEllipsis) {
    pages.push("right-ellipsis");
  } else {
    for (let page = rightSiblingIndex + 1; page < totalPages; page += 1) {
      pages.push(page);
    }
  }

  pages.push(totalPages);

  return pages;
}


function FilterAccordionSection({ title, sectionKey, openSection, onToggle, children }) {
  const isOpen = Array.isArray(openSection)
    ? openSection.includes(sectionKey)
    : openSection === sectionKey;

  return (
    <div className={`kyb-section kyb-section--accordion ${isOpen ? "is-open" : ""}`}>
      <button
        type="button"
        className="kyb-section-toggle"
        onClick={() => onToggle(sectionKey)}
        aria-expanded={isOpen}
      >
        <h3 className="kyb-section-header-title">{title}</h3>
        <span className="kyb-section-toggle-icon" aria-hidden="true">
          {isOpen ? <Minus size={18} /> : <Plus size={18} />}
        </span>
      </button>
      {isOpen ? <div className="kyb-section-body">{children}</div> : null}
    </div>
  );
}

function CourseDetailsFields({
  openSection,
  onToggle,
  isInitialLoading,
  loading,
  formData,
  options,
  validationErrors,
  menuPortalTarget,
  setInstitutionSearch,
  setFormData,
  handleInstitutionSelect,
  renderSelectField,
  isCourseTypeAutoFilled,
}) {
  return (
    <FilterAccordionSection
      title="Course Details"
      sectionKey="courseDetails"
      openSection={openSection}
      onToggle={onToggle}
    >
        <div className="kyb-field-grid kyb-field-grid--single">
          {renderSelectField(
            "admissionThroughCET",
            "Whether your admission was done through the Central admission process of CET cell of Maharashtra?",
            yesNoOptions,
            { disabled: isInitialLoading, placeholder: "Select" }
          )}
          {renderSelectField(
            "completed10thFromGovernmentVidyaniketan",
            "Whether you completed your 10th grade from a Government Vidyaniketan school?",
            yesNoOptions,
            { disabled: isInitialLoading, placeholder: "Select" }
          )}
          {renderSelectField("stateId", "State", options.states, {
            disabled: isInitialLoading,
            placeholder: "Select",
          })}
          {renderSelectField("districtId", "District", options.districts, {
            disabled: isInitialLoading || loading.districts || !formData.stateId,
            placeholder: formData.stateId
              ? loading.districts
                ? "Loading districts..."
                : "Select"
              : "Select state first",
          })}
          {renderSelectField("talukaId", "Taluka", options.talukas, {
            disabled: isInitialLoading || loading.talukas || !formData.districtId,
            placeholder: formData.districtId
              ? loading.talukas
                ? "Loading talukas..."
                : "Select"
              : "Select",
          })}

          <div className="kyb-field">
            <label className="kyb-field-label" htmlFor="universityId-select">
              Institute Name
              <span className="kyb-required">*</span>
            </label>
            <Select
              inputId="universityId-select"
              options={options.institutions}
              className={`react-select-container ${validationErrors.universityId ? "is-invalid" : ""}`}
              classNamePrefix="react-select"
              placeholder={
                formData.talukaId
                  ? loading.institutions
                    ? "Searching..."
                    : "Type to search (min 2 chars)"
                  : "Select"
              }
              isDisabled={isInitialLoading || !formData.talukaId}
              isSearchable
              isClearable={false}
              value={
                options.institutions.find(
                  (o) => String(o.value) === String(formData.universityId || "")
                ) || null
              }
              onChange={(selected) => handleInstitutionSelect(selected)}
              onInputChange={(val, meta) => {
                if (meta.action === "input-change") {
                  setInstitutionSearch(val);
                }
              }}
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
              noOptionsMessage={({ inputValue }) =>
                inputValue ? "No matching options" : "Type to search"
              }
            />
            {validationErrors.universityId ? (
              <small className="kyb-field-error">{validationErrors.universityId}</small>
            ) : null}
          </div>

          <div className="kyb-field">
            <label className="kyb-field-label" htmlFor="universityName">
              University Name
            </label>
            <input
              id="universityName"
              type="text"
              className="kyb-control"
              placeholder="University name"
              value={formData.universityName || ""}
              onChange={(e) =>
                setFormData((current) => ({
                  ...current,
                  universityName: e.target.value,
                }))
              }
              disabled={isInitialLoading}
            />
          </div>
          {renderSelectField(
            "currentCourseName",
            "Current course name",
            options.courseNames,
            {
              disabled:
                isInitialLoading ||
                loading.courses ||
                !String(formData.universityId || "").trim(),
              placeholder: String(formData.universityId || "").trim()
                ? loading.courses
                  ? "Loading courses..."
                  : "Select"
                : "Select ",
            }
          )}
          {isCourseTypeAutoFilled ? (
            <div className="kyb-field">
              <label className="kyb-field-label" htmlFor="courseTypeAuto">
                Course Type
              </label>
              <input
                id="courseTypeAuto"
                type="text"
                className="kyb-control"
                value={formData.courseTypeLabel || ""}
                disabled={true}
              />
            </div>
          ) : (
            renderSelectField(
              "courseType",
              "Course Type",
              courseTypeOptions,
              {
                disabled:
                  isInitialLoading || !String(formData.currentCourseName || "").trim(),
                placeholder: String(formData.currentCourseName || "").trim()
                  ? "Select"
                  : "Select course name first",
              }
            )
          )}
          {renderSelectField(
            "yearOfStudyOrCourseLevel",
            "Year of study / Course level",
            options.qualificationLevels,
            {
              disabled: isInitialLoading,
              placeholder: options.qualificationLevels?.length
                ? "Select"
                : "No options available",
            }
          )}
        </div>
    </FilterAccordionSection>
  );
}

function GradeAndSchoolDetailsFields({
  openSection,
  onToggle,
  isInitialLoading,
  renderSelectField,
  renderInputField,
}) {
  return (
    <FilterAccordionSection
      title="Grade and School Details"
      sectionKey="gradeAndSchoolDetails"
      openSection={openSection}
      onToggle={onToggle}
    >
        <div className="kyb-field-grid kyb-field-grid--single">
          {renderSelectField(
            "yearOfStudyOrCourseLevel",
            "Year of Study / Standard",
            preMatricStandardOptions,
            {
              disabled: isInitialLoading,
              placeholder: "Select",
            }
          )}
          {renderInputField("udiseCodeOfSchool", "UDISE code of school", "text", {
            disabled: isInitialLoading,
            placeholder: "Enter UDISE code of school",
          })}
        </div>
    </FilterAccordionSection>
  );
}

function normalizeOptionLabelForSort(label) {
  const raw = String(label || "").trim();
  if (!raw) return "";
  return raw
    .replace(/^\(([^)]+)\)\s*/u, "$1 ")
    .replace(/\s+/gu, " ")
    .trim();
}

function resolveDepartmentDisplayName(scheme, departmentNameById) {
  const raw = scheme?.raw || {};
  const deptId = String(raw.department ?? scheme?.department ?? "").trim();
  const fromApi =
    String(raw.departmentName || raw.departmentLabel || "").trim() ||
    String(scheme?.departmentName || scheme?.departmentLabel || "").trim();
  if (fromApi) return fromApi;
  if (deptId && departmentNameById[deptId]) return departmentNameById[deptId];
  const fallback = String(scheme?.department || "").trim();
  if (/^\d+$/.test(fallback) && departmentNameById[fallback]) {
    return departmentNameById[fallback];
  }
  return fallback || "-";
}

function sortOptionsByLabel(fieldOptions) {
  if (!Array.isArray(fieldOptions)) return [];
  return fieldOptions
    .map((opt, idx) => ({ opt, idx }))
    .sort((a, b) => {
      const aLabel = normalizeOptionLabelForSort(a?.opt?.label);
      const bLabel = normalizeOptionLabelForSort(b?.opt?.label);
      const cmp = aLabel.localeCompare(bLabel, undefined, {
        sensitivity: "base",
        numeric: true,
      });
      if (cmp !== 0) return cmp;
      return a.idx - b.idx; // stable fallback
    })
    .map(({ opt }) => opt);
}

function normalizeAutofillText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function getAutofillComparableValues(rawValue) {
  const normalizedValue = normalizeAutofillText(rawValue);
  const values = new Set();

  if (normalizedValue) {
    values.add(normalizedValue);
  }

  if (normalizedValue === "m") {
    values.add("male");
  } else if (normalizedValue === "f") {
    values.add("female");
  } else if (normalizedValue === "t" || normalizedValue === "o") {
    values.add("transgender");
    values.add("other");
  }

  return values;
}

function parseAadhaarDemographicDetails(response) {
  try {
    const demographicDetails = response?.aadharProfile || response?.demographicDetails;
    if (!demographicDetails) {
      return null;
    }

    const parsed =
      typeof demographicDetails === "string"
        ? JSON.parse(demographicDetails)
        : demographicDetails;

    return parsed?.data || null;
  } catch {
    return null;
  }
}

function findMatchingOption(options = [], rawValue) {
  const comparableValues = getAutofillComparableValues(rawValue);
  if (comparableValues.size === 0) {
    return null;
  }

  return (
    (Array.isArray(options) ? options : []).find((option) => {
      const candidates = [
        option?.label,
        option?.value,
        option?.name,
        option?.key,
      ];

      return candidates.some((candidate) =>
        comparableValues.has(normalizeAutofillText(candidate)),
      );
    }) || null
  );
}

function normalizeMahaProfileKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function getMahaProfilePrimitiveValue(item) {
  const candidates = [
    item?.value,
    item?.fieldValue,
    item?.attributeValue,
    item?.answer,
    item?.response,
    item?.selectedValue,
    item?.displayValue,
    item?.name,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue;
    if (typeof candidate === "string" || typeof candidate === "number") {
      const normalized = String(candidate).trim();
      if (normalized) return normalized;
    }

    if (typeof candidate === "object") {
      const nested = candidate?.label || candidate?.name || candidate?.value;
      if (nested !== null && nested !== undefined && String(nested).trim()) {
        return String(nested).trim();
      }
    }
  }

  return "";
}

function extractMahaProfileValue(profileItems = [], candidateKeys = []) {
  const normalizedKeys = candidateKeys
    .map((key) => normalizeMahaProfileKey(key))
    .filter(Boolean);

  if (!normalizedKeys.length) {
    return "";
  }

  for (const item of Array.isArray(profileItems) ? profileItems : []) {
    const itemKeys = [
      item?.grmappingkey,
      item?.portalAttribute,
      item?.fieldName,
      item?.actualFieldName,
      item?.attributeName,
      item?.attributename,
      item?.sourceName,
      item?.label,
      item?.name,
    ]
      .map((value) => normalizeMahaProfileKey(value))
      .filter(Boolean);

    if (!itemKeys.some((itemKey) => normalizedKeys.includes(itemKey))) {
      continue;
    }

    const itemValue = getMahaProfilePrimitiveValue(item);
    if (itemValue) {
      return itemValue;
    }
  }

  return "";
}

function KnowYourEligibilityForm() {
  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  const [formData, setFormData] = useState(initialForm);
  const [options, setOptions] = useState({
    beneficiaryCategories: [],
    religions: [],
    genders: [],
    casteCategories: [],
    castes: [],
    courseNames: [],
    states: [],
    districts: [],
    talukas: [],
    institutions: [],
    qualificationLevels: [],
    birthOrders: [],
  });
  const [loading, setLoading] = useState({
    initial: true,
    castes: false,
    courses: false,
    districts: false,
    talukas: false,
    institutions: false,
    results: false,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState("");
  const [eligibleSchemes, setEligibleSchemes] = useState([]);
  const [resultsPage, setResultsPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const pageSizeOptions = [5, 10, 20];
  const [departmentNameById, setDepartmentNameById] = useState({});
  const [appliedBySchemeId, setAppliedBySchemeId] = useState({});
  const [appliedLoadingBySchemeId, setAppliedLoadingBySchemeId] = useState({});
  const [descriptionModal, setDescriptionModal] = useState({
    show: false,
    title: "",
    description: "",
    department: "",
    schemeData: null,
  });
  const [applyModal, setApplyModal] = useState({
    show: false,
    schemeId: "",
    schemeName: "",
    title: "",
    message: "",
    variant: "info",
    canProceed: false,
    academicYears: [],
    selectedAcademicYear: "",
    loading: false,
    steps: [
      { id: 1, name: "Check previous applications", status: "pending", result: "" },
      { id: 2, name: "Validate eligibility rules", status: "pending", result: "" },
      { id: 3, name: "Final result", status: "pending", result: "" },
    ],
  });
  const [grDownloadModal, setGrDownloadModal] = useState({
    show: false,
    schemeName: "",
    schemeGr: "",
  });
  const [benefitModal, setBenefitModal] = useState({
    show: false,
    title: "",
    message: "",
    variant: "info",
    schemeName: "",
    benefitHtml: "",
    benefitsData: null,
    loading: false,
    error: "",
    benefitsError: "",
    benefitsErrorList: [],
  });
  const [schemeSearch, setSchemeSearch] = useState("");
  const [isCourseTypeAutoFilled, setIsCourseTypeAutoFilled] = useState(false);
  const [isHeadMasterUser, setIsHeadMasterUser] = useState(false);
  const [isRoleResolved, setIsRoleResolved] = useState(
    !Boolean(window?.Liferay?.ThemeDisplay?.isSignedIn?.())
  );
  const [appliedStatusRefreshKey, setAppliedStatusRefreshKey] = useState(0);

  const requiredFields = useMemo(() => [], []);

  const [institutionSearch, setInstitutionSearch] = useState("");
  const [mahaProfileData, setMahaProfileData] = useState([]);
  const [storedAadhaarProfile, setStoredAadhaarProfile] = useState(null);
  const [openSection, setOpenSection] = useState(["schemeDetails"]);

  const handleSectionToggle = useCallback((sectionKey) => {
    setOpenSection((current) => {
      const currentSections = Array.isArray(current) ? current : [];
      return currentSections.includes(sectionKey)
        ? currentSections.filter((key) => key !== sectionKey)
        : [...currentSections, sectionKey];
    });
  }, []);
  const mahaAutofillAppliedRef = useRef({
    religion: false,
    casteCategory: false,
    caste: false,
    gender: false,
    disability: false,
    domicileOfMaharashtra: false,
    familyAnnualIncome: false,
    birthOrder: false,
    stateId: false,
  });
  const aadhaarAutofillAppliedRef = useRef({
    gender: false,
    domicileOfMaharashtra: false,
    stateId: false,
  });

  const maharashtraStateId = useMemo(() => {
    const states = options.states || [];
    const defaultState =
      states.find(
        (s) => String(s?.label || "").trim().toLowerCase() === "maharashtra"
      ) ||
      states.find((s) =>
        String(s?.label || "")
          .trim()
          .toLowerCase()
          .includes("maharashtra")
      );

    return defaultState?.value ? String(defaultState.value) : "";
  }, [options.states]);

  const isGuestUser = useMemo(() => {
    const signedInFlag = window?.Liferay?.ThemeDisplay?.isSignedIn;

    if (typeof signedInFlag === "function") {
      return signedInFlag() !== true;
    }

    if (typeof signedInFlag === "boolean") {
      return signedInFlag !== true;
    }

    return Number(window?.Liferay?.ThemeDisplay?.getUserId?.() || 0) <= 0;
  }, []);

  const sortedCasteCategoryOptions = useMemo(
    () => sortOptionsByLabel(options.casteCategories),
    [options.casteCategories]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadUserRole() {
      try {
        const userId = window.Liferay?.ThemeDisplay?.getUserId?.();
        if (!userId) {
          setIsRoleResolved(true);
          return;
        }

        const userData = await getUserRolesById(userId);
        if (!isMounted) return;

        const hasHeadMasterRole = Boolean(
          userData?.roleBriefs?.some((role) => role?.name === "Head Master")
        );
        setIsHeadMasterUser(hasHeadMasterRole);
        if (hasHeadMasterRole) {
          setFormData((current) => ({ ...current, schemeType: ["preMatric"] }));
        }
      } catch (error) {
        console.error("Failed to load user roles:", error);
      } finally {
        if (isMounted) setIsRoleResolved(true);
      }
    }

    loadUserRole();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const collegeId = String(formData.universityId || "").trim();
    const courseId = String(formData.currentCourseName || "").trim();
    if (!collegeId || !courseId) return;

    let cancelled = false;
    (async () => {
      try {
        const isProfessional = await fetchIsProfessionalFromCollegeAndCourse(
          collegeId,
          courseId
        );
        if (cancelled) return;
        const isProfessionalCourse = String(isProfessional) === "1";
        const courseTypeValue = isProfessionalCourse ? "professional" : "nonProfessional";
        const courseTypeLabel = isProfessionalCourse ? "Professional" : "Non Professional";
        setFormData((current) => ({
          ...current,
          courseType: courseTypeValue,
          courseTypeLabel,
        }));
        setIsCourseTypeAutoFilled(true);
      } catch (e) {
        console.warn("Course type autofill failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.universityId, formData.currentCourseName]);

  useEffect(() => {
    let isMounted = true;

    async function loadInitial() {
      try {
        setLoading((c) => ({ ...c, initial: true }));
        setError("");

        const results = await Promise.allSettled([
          fetchReligionOptions(),
          fetchGenderOptions(),
          fetchCasteCategoryOptions(),
          fetchBeneficiaryCategoryOptions(),
          fetchStateOptions(),
          fetchDepartmentsOptions(),
          fetchQualificationLevelOptions(),
          fetchBirthOrderOptions(),
        ]);

        if (!isMounted) return;

        const next = {
          religions: results[0].status === "fulfilled" ? results[0].value : [],
          genders: results[1].status === "fulfilled" ? results[1].value : [],
          casteCategories:
            results[2].status === "fulfilled" ? results[2].value : [],
          beneficiaryCategories:
            results[3].status === "fulfilled" ? results[3].value : [],
          states: results[4].status === "fulfilled" ? results[4].value : [],
          courseNames: [],
          castes: [],
          districts: [],
          talukas: [],
          institutions: [],
          qualificationLevels:
            results[6].status === "fulfilled" ? results[6].value : [],
          birthOrders: results[7].status === "fulfilled" ? results[7].value : [],
        };

        setOptions(next);

        if (results[5].status === "fulfilled" && Array.isArray(results[5].value)) {
          const map = {};
          for (const row of results[5].value) {
            const id = String(row?.id ?? "").trim();
            const name = String(row?.name ?? "").trim();
            if (id && name) map[id] = name;
          }
          setDepartmentNameById(map);
        }

      } catch (e) {
        console.error("Failed to load master data:", e);
        if (isMounted) setError("Failed to load dropdown values.");
      } finally {
        if (isMounted) setLoading((c) => ({ ...c, initial: false }));
      }
    }

    loadInitial();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadMahaProfile() {
      try {
        const loginUserId = Number(window.Liferay?.ThemeDisplay?.getUserId?.() || 0);
        if (!loginUserId) {
          return;
        }

        const mahaId = await fetchJobTitle(loginUserId);
        if (!isMounted) return;

        if (!String(mahaId || "").trim() || String(mahaId).trim() === "0") {
          return;
        }

        const response = await fetchApiDataBaseOnSchemeIdAndMahaId(0, mahaId);
        if (!isMounted) return;

        const citizenDatas = Array.isArray(response?.citizenDatas)
          ? response.citizenDatas
          : Array.isArray(response)
            ? response
            : [];

        if (citizenDatas.length > 0) {
          setMahaProfileData(citizenDatas);
        }
      } catch (error) {
        console.error("Failed to load Maha profile data:", error);
      }
    }

    async function loadStoredAadhaarProfile() {
      try {
        const normalizedUserEmail = String(
          window.Liferay?.ThemeDisplay?.getUserEmailAddress?.() || "",
        )
          .trim()
          .toLowerCase();

        if (!normalizedUserEmail) {
          return;
        }

        const response = await fetchUserAadhaarProfileByEmail(normalizedUserEmail);
        if (!isMounted) return;

        if (response?.aadharProfile) {
          setStoredAadhaarProfile(response);
        }
      } catch (error) {
        console.error("Failed to load stored Aadhaar profile:", error);
      }
    }

    loadMahaProfile();
    loadStoredAadhaarProfile();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(mahaProfileData) || mahaProfileData.length === 0) {
      return;
    }

    const religionOption = findMatchingOption(options.religions, extractMahaProfileValue(
      mahaProfileData,
      ["religion", "applicantreligion", "religionid"],
    ));
    const casteCategoryOption = findMatchingOption(
      sortedCasteCategoryOptions,
      extractMahaProfileValue(mahaProfileData, [
        "castecategory",
        "category",
        "socialcategory",
        "applicantcategory",
      ]),
    );
    const genderOption = findMatchingOption(
      options.genders,
      extractMahaProfileValue(mahaProfileData, ["gender", "applicantgender"]),
    );
    const disabilityRawValue = extractMahaProfileValue(mahaProfileData, [
      "disability",
      "isdisabled",
      "pwd",
      "personwithdisability",
    ]);
    const domicileRawValue = extractMahaProfileValue(mahaProfileData, [
      "domicileofmaharashtra",
      "domicile",
      "maharashtradomicile",
      "ismhdomicile",
    ]);
    const incomeRawValue = extractMahaProfileValue(mahaProfileData, [
      "familyannualincome",
      "annualincome",
      "familyincome",
      "parentannualincome",
      "income",
    ]);
    const birthOrderOption = findMatchingOption(
      options.birthOrders,
      extractMahaProfileValue(mahaProfileData, ["birthorder", "applicantbirthorder"]),
    );
    const stateOption = findMatchingOption(
      options.states,
      extractMahaProfileValue(mahaProfileData, [
        "state",
        "stateid",
        "permanentstate",
        "addressstate",
      ]),
    );
    const stateRawValue = extractMahaProfileValue(mahaProfileData, [
      "state",
      "stateid",
      "permanentstate",
      "addressstate",
    ]);
    const isMaharashtraFromMahaState =
      normalizeAutofillText(stateRawValue) === normalizeAutofillText("Maharashtra") ||
      normalizeAutofillText(stateOption?.label) === normalizeAutofillText("Maharashtra") ||
      (stateOption?.value &&
        maharashtraStateId &&
        String(stateOption.value) === String(maharashtraStateId));

    setFormData((current) => {
      const next = { ...current };

      if (
        !mahaAutofillAppliedRef.current.religion &&
        !String(current.religion || "").trim() &&
        religionOption?.value
      ) {
        next.religion = String(religionOption.value);
        mahaAutofillAppliedRef.current.religion = true;
      }

      if (
        !mahaAutofillAppliedRef.current.casteCategory &&
        !String(current.casteCategory || "").trim() &&
        casteCategoryOption?.value
      ) {
        next.casteCategory = String(casteCategoryOption.value);
        mahaAutofillAppliedRef.current.casteCategory = true;
      }

      if (
        !mahaAutofillAppliedRef.current.gender &&
        !String(current.gender || "").trim() &&
        genderOption?.value
      ) {
        next.gender = String(genderOption.value);
        mahaAutofillAppliedRef.current.gender = true;
      }

      if (
        !mahaAutofillAppliedRef.current.disability &&
        !String(current.disability || "").trim() &&
        String(disabilityRawValue || "").trim()
      ) {
        const normalizedDisability = normalizeAutofillText(disabilityRawValue);
        next.disability =
          normalizedDisability === "yes" ||
          normalizedDisability === "true" ||
          normalizedDisability === "1"
            ? "Yes"
            : "No";
        mahaAutofillAppliedRef.current.disability = true;
      }

      if (
        !mahaAutofillAppliedRef.current.domicileOfMaharashtra &&
        !String(current.domicileOfMaharashtra || "").trim() &&
        (String(domicileRawValue || "").trim() || isMaharashtraFromMahaState)
      ) {
        if (isMaharashtraFromMahaState) {
          next.domicileOfMaharashtra = "Yes";
        } else {
          const normalizedDomicile = normalizeAutofillText(domicileRawValue);
          next.domicileOfMaharashtra =
            normalizedDomicile === "yes" ||
            normalizedDomicile === "true" ||
            normalizedDomicile === "1" ||
            normalizedDomicile === "maharashtra"
              ? "Yes"
              : "No";
        }
        mahaAutofillAppliedRef.current.domicileOfMaharashtra = true;
      }

      if (
        !mahaAutofillAppliedRef.current.familyAnnualIncome &&
        !String(current.familyAnnualIncome || "").trim() &&
        String(incomeRawValue || "").trim()
      ) {
        next.familyAnnualIncome = String(incomeRawValue).replace(/[^\d.]/g, "");
        mahaAutofillAppliedRef.current.familyAnnualIncome = true;
      }

      if (
        !mahaAutofillAppliedRef.current.birthOrder &&
        !String(current.birthOrder || "").trim() &&
        birthOrderOption?.value
      ) {
        next.birthOrder = String(birthOrderOption.value);
        next.birthOrderLabel = String(birthOrderOption.label || "");
        mahaAutofillAppliedRef.current.birthOrder = true;
      }

      if (
        !mahaAutofillAppliedRef.current.stateId &&
        !String(current.stateId || "").trim() &&
        stateOption?.value
      ) {
        next.stateId = String(stateOption.value);
        mahaAutofillAppliedRef.current.stateId = true;
      }

      return next;
    });
  }, [
    mahaProfileData,
    maharashtraStateId,
    options.genders,
    options.religions,
    options.states,
    options.birthOrders,
    sortedCasteCategoryOptions,
  ]);

  useEffect(() => {
    if (!Array.isArray(mahaProfileData) || mahaProfileData.length === 0) {
      return;
    }

    const casteOption = findMatchingOption(
      options.castes,
      extractMahaProfileValue(mahaProfileData, ["caste", "applicantcaste"]),
    );

    if (!casteOption?.value) {
      return;
    }

    setFormData((current) => {
      if (
        mahaAutofillAppliedRef.current.caste ||
        String(current.caste || "").trim()
      ) {
        return current;
      }

      mahaAutofillAppliedRef.current.caste = true;
      return {
        ...current,
        caste: String(casteOption.value),
      };
    });
  }, [mahaProfileData, options.castes]);

  useEffect(() => {
    const demographicData = parseAadhaarDemographicDetails(storedAadhaarProfile);
    if (!demographicData) {
      return;
    }

    const poi = demographicData?.poi || {};
    const address = demographicData?.address || {};

    const genderOption = findMatchingOption(options.genders, poi?.gender);
    const stateOption = findMatchingOption(options.states, address?.state);
    const isMaharashtra =
      normalizeAutofillText(address?.state) === normalizeAutofillText("Maharashtra");

    setFormData((current) => {
      const next = { ...current };

      if (
        !aadhaarAutofillAppliedRef.current.gender &&
        !String(current.gender || "").trim() &&
        genderOption?.value
      ) {
        next.gender = String(genderOption.value);
        aadhaarAutofillAppliedRef.current.gender = true;
      }

      if (
        !aadhaarAutofillAppliedRef.current.domicileOfMaharashtra &&
        !String(current.domicileOfMaharashtra || "").trim() &&
        String(address?.state || "").trim()
      ) {
        next.domicileOfMaharashtra = isMaharashtra ? "Yes" : "No";
        aadhaarAutofillAppliedRef.current.domicileOfMaharashtra = true;
      }

      if (
        !aadhaarAutofillAppliedRef.current.stateId &&
        (
          !String(current.stateId || "").trim() ||
          String(current.stateId) === String(maharashtraStateId || "")
        ) &&
        stateOption?.value
      ) {
        next.stateId = String(stateOption.value);
        aadhaarAutofillAppliedRef.current.stateId = true;
      }

      return next;
    });
  }, [maharashtraStateId, options.genders, options.states, storedAadhaarProfile]);

  useEffect(() => {
    let isMounted = true;

    async function loadCastes() {
      if (!formData.casteCategory) {
        setOptions((c) => ({ ...c, castes: [] }));
        return;
      }

      try {
        setLoading((c) => ({ ...c, castes: true }));
        const castes = await fetchCasteOptions(formData.casteCategory);
        if (!isMounted) return;
        setOptions((c) => ({ ...c, castes }));
      } catch (e) {
        console.error("Failed to load castes:", e);
        if (isMounted) setOptions((c) => ({ ...c, castes: [] }));
      } finally {
        if (isMounted) setLoading((c) => ({ ...c, castes: false }));
      }
    }

    loadCastes();
    return () => {
      isMounted = false;
    };
  }, [formData.casteCategory]);

  const shouldRenderCasteField = useMemo(() => {
    if (!String(formData.casteCategory || "").trim()) return false;
    if (loading.castes) return true;
    return Array.isArray(options.castes) && options.castes.length > 0;
  }, [formData.casteCategory, loading.castes, options.castes]);

  useEffect(() => {
    if (shouldRenderCasteField) return;
    if (!String(formData.caste || "").trim() && !validationErrors.caste) return;

    setFormData((c) => ({ ...c, caste: "" }));
    if (validationErrors.caste) {
      setValidationErrors((current) => {
        const next = { ...current };
        delete next.caste;
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRenderCasteField]);


  useEffect(() => {
    let isMounted = true;

    async function loadDistricts() {
      if (!formData.stateId) {
        setOptions((c) => ({ ...c, districts: [], talukas: [], institutions: [] }));
        return;
      }

      try {
        setLoading((c) => ({ ...c, districts: true }));
        const districts = await fetchDistrictOptionsByState(formData.stateId);
        if (!isMounted) return;
        setOptions((c) => ({ ...c, districts, talukas: [], institutions: [] }));
      } catch (e) {
        console.error("Failed to load districts:", e);
        if (isMounted) setOptions((c) => ({ ...c, districts: [], talukas: [], institutions: [] }));
      } finally {
        if (isMounted) setLoading((c) => ({ ...c, districts: false }));
      }
    }

    loadDistricts();
    return () => {
      isMounted = false;
    };
  }, [formData.stateId]);

  useEffect(() => {
    let isMounted = true;

    async function loadTalukas() {
      if (!formData.districtId) {
        setOptions((c) => ({ ...c, talukas: [], institutions: [] }));
        return;
      }

      try {
        setLoading((c) => ({ ...c, talukas: true }));
        const talukas = await fetchTalukaOptionsByDistrict(formData.districtId);
        if (!isMounted) return;
        setOptions((c) => ({ ...c, talukas, institutions: [] }));
      } catch (e) {
        console.error("Failed to load talukas:", e);
        if (isMounted) setOptions((c) => ({ ...c, talukas: [], institutions: [] }));
      } finally {
        if (isMounted) setLoading((c) => ({ ...c, talukas: false }));
      }
    }

    loadTalukas();
    return () => {
      isMounted = false;
    };
  }, [formData.districtId]);

  useEffect(() => {
    let isMounted = true;
    const debounceMs = 350;
    const q = String(institutionSearch || "").trim();

    if (!formData.talukaId) {
      setOptions((c) => ({ ...c, institutions: [] }));
      setLoading((c) => ({ ...c, institutions: false }));
      return () => {
        isMounted = false;
      };
    }

    const timer = setTimeout(async () => {
      try {
        if (isMounted) setLoading((c) => ({ ...c, institutions: true }));
        const institutions = await fetchInstitutionOptions({
          search: q,
          stateId: formData.stateId,
          districtId: formData.districtId,
          talukaId: formData.talukaId,
        });
        if (!isMounted) return;
        setOptions((c) => ({ ...c, institutions }));
      } catch (e) {
        console.error("Failed to load institutions:", e);
        if (isMounted) setOptions((c) => ({ ...c, institutions: [] }));
      } finally {
        if (isMounted) setLoading((c) => ({ ...c, institutions: false }));
      }
    }, debounceMs);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [institutionSearch, formData.stateId, formData.districtId, formData.talukaId]);

  useEffect(() => {
    let isMounted = true;
    const debounceMs = 500;
    const collegeId = String(formData.universityId || "").trim();

    if (!collegeId) {
      setOptions((current) => ({ ...current, courseNames: [] }));
      setLoading((c) => ({ ...c, courses: false }));
      return () => {
        isMounted = false;
      };
    }

    const timer = setTimeout(async () => {
      try {
        if (isMounted) setLoading((c) => ({ ...c, courses: true }));
        const courses = await fetchCoursesForCollegeId(collegeId);
        if (!isMounted) return;
        setOptions((current) => ({ ...current, courseNames: courses }));
      } catch (e) {
        console.error("Failed to load current courses:", e);
        if (isMounted) {
          setOptions((current) => ({ ...current, courseNames: [] }));
        }
      } finally {
        if (isMounted) setLoading((c) => ({ ...c, courses: false }));
      }
    }, debounceMs);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [formData.universityId]);

  function handleSelectChange(name, value, selectedOption = null) {
    const selectedLabel = String(selectedOption?.label || "");

    setFormData((current) => {
      const nextState = { ...current, [name]: value };
      if (name === "casteCategory") {
        nextState.caste = "";
        nextState.casteCategoryLabel = selectedLabel;
      }
      if (name === "currentCourseName") {
        nextState.courseType = "";
        nextState.courseTypeLabel = "";
        nextState.yearOfStudyOrCourseLevel = "";
        nextState.yearOfStudyOrCourseLevelLabel = "";
        nextState.currentCourseNameLabel = selectedLabel;
      }
      if (name === "courseType") {
        nextState.courseTypeLabel = selectedLabel;
      }
      if (name === "birthOrder") {
        nextState.birthOrderLabel = selectedLabel;
      }
      if (name === "yearOfStudyOrCourseLevel") {
        nextState.yearOfStudyOrCourseLevelLabel = selectedLabel;
      }
      if (name === "schemeType") {
        nextState.admissionThroughCET = "";
        nextState.completed10thFromGovernmentVidyaniketan = "";
        nextState.alpaBhuOrRegisteredLabour = "";
        nextState.hazardousOrUncleanOccupation = "";
        nextState.stateId = current.stateId;
        nextState.districtId = "";
        nextState.districtLabel = "";
        nextState.talukaId = "";
        nextState.talukaLabel = "";
        nextState.universityId = "";
        nextState.institutionName = "";
        nextState.universityName = "";
        nextState.currentCourseName = "";
        nextState.currentCourseNameLabel = "";
        nextState.courseType = "";
        nextState.courseTypeLabel = "";
        nextState.yearOfStudyOrCourseLevel = "";
        nextState.yearOfStudyOrCourseLevelLabel = "";
        nextState.udiseCodeOfSchool = "";
      }
      if (name === "religion") {
        nextState.casteCategory = "";
        nextState.casteCategoryLabel = "";
        nextState.caste = "";
      }
      if (name === "stateId") {
        nextState.stateLabel = selectedLabel;
        nextState.districtId = "";
        nextState.districtLabel = "";
        nextState.talukaId = "";
        nextState.talukaLabel = "";
        nextState.universityId = "";
        nextState.institutionName = "";
        nextState.universityName = "";
        nextState.currentCourseName = "";
        nextState.currentCourseNameLabel = "";
        nextState.courseType = "";
        nextState.courseTypeLabel = "";
        nextState.yearOfStudyOrCourseLevel = "";
        nextState.yearOfStudyOrCourseLevelLabel = "";
      }
      if (name === "districtId") {
        nextState.districtLabel = selectedLabel;
        nextState.talukaId = "";
        nextState.talukaLabel = "";
        nextState.universityId = "";
        nextState.institutionName = "";
        nextState.universityName = "";
        nextState.currentCourseName = "";
        nextState.currentCourseNameLabel = "";
        nextState.courseType = "";
        nextState.courseTypeLabel = "";
        nextState.yearOfStudyOrCourseLevel = "";
        nextState.yearOfStudyOrCourseLevelLabel = "";
      }
      if (name === "talukaId") {
        nextState.talukaLabel = selectedLabel;
        nextState.universityId = "";
        nextState.institutionName = "";
        nextState.universityName = "";
        nextState.currentCourseName = "";
        nextState.currentCourseNameLabel = "";
        nextState.courseType = "";
        nextState.courseTypeLabel = "";
        nextState.yearOfStudyOrCourseLevel = "";
        nextState.yearOfStudyOrCourseLevelLabel = "";
      }
      return nextState;
    });

    if (name === "currentCourseName" || name === "courseType") {
      setIsCourseTypeAutoFilled(false);
    }

    if (validationErrors[name]) {
      setValidationErrors((current) => {
        const next = { ...current };
        delete next[name];
        if (name === "casteCategory") {
          delete next.caste;
        }
        if (name === "schemeType") {
          delete next.alpaBhuOrRegisteredLabour;
          delete next.hazardousOrUncleanOccupation;
          delete next.admissionThroughCET;
          delete next.completed10thFromGovernmentVidyaniketan;
          delete next.stateId;
          delete next.districtId;
          delete next.talukaId;
          delete next.universityId;
          delete next.institutionName;
          delete next.currentCourseName;
          delete next.yearOfStudyOrCourseLevel;
          delete next.udiseCodeOfSchool;
        }
        if (name === "currentCourseName") {
          delete next.courseType;
          delete next.yearOfStudyOrCourseLevel;
        }
        if (name === "religion") {
          delete next.casteCategory;
          delete next.caste;
        }
        if (name === "stateId") {
          delete next.districtId;
          delete next.talukaId;
          delete next.universityId;
          delete next.institutionName;
          delete next.currentCourseName;
          delete next.courseType;
          delete next.yearOfStudyOrCourseLevel;
        }
        if (name === "districtId") {
          delete next.talukaId;
          delete next.universityId;
          delete next.institutionName;
          delete next.currentCourseName;
          delete next.courseType;
          delete next.yearOfStudyOrCourseLevel;
        }
        if (name === "talukaId") {
          delete next.universityId;
          delete next.institutionName;
          delete next.currentCourseName;
          delete next.courseType;
          delete next.yearOfStudyOrCourseLevel;
        }
        return next;
      });
    }
  }

  function extractUniversityNameFromCollege(college) {
    if (!college || typeof college !== "object") return "";

    const candidates = [
      college.universityName,
      college.university,
      college.universityMasterName,
      college.universitymastername,
      college.university_name,
      college.universityname,
      college.r_university_c_universityMasterName,
      college.r_university_c_universitymastername,
      college.r_university_c_universityName,
      college.r_university_c_universityname,
    ];

    for (const val of candidates) {
      const s = String(val ?? "").trim();
      if (s) return s;
    }

    const rel = college.universityObject || college.universityDetails || college.r_university || null;
    if (rel && typeof rel === "object") {
      const s = String(rel.name ?? rel.universityName ?? "").trim();
      if (s) return s;
    }

    return "";
  }

  async function handleInstitutionSelect(selected) {
    const id = String(selected?.value || "");
    const label = String(selected?.label || "");

    setFormData((current) => ({
      ...current,
      universityId: id,
      institutionName: label,
      universityName: "",
      currentCourseName: "",
      currentCourseNameLabel: "",
      courseType: "",
      courseTypeLabel: "",
      yearOfStudyOrCourseLevel: "",
      yearOfStudyOrCourseLevelLabel: "",
    }));
    setIsCourseTypeAutoFilled(false);

    if (id) {
      const college = await fetchCollegeById(id);
      const universityName = extractUniversityNameFromCollege(college);
      setFormData((current) => ({
        ...current,
        universityName: universityName || "",
      }));
    }

    if (validationErrors.universityId || validationErrors.currentCourseName) {
      setValidationErrors((current) => {
        const next = { ...current };
        delete next.universityId;
        delete next.currentCourseName;
        delete next.courseType;
        delete next.yearOfStudyOrCourseLevel;
        return next;
      });
    }
  }

  function renderSelectField(
    name,
    label,
    fieldOptions,
    config = { disabled: false, placeholder: "" }
  ) {
    const selectedOption = fieldOptions.find(
      (option) => String(option.value) === String(formData[name] || "")
    );

    return (
      <div className="kyb-field">
        <label className="kyb-field-label" htmlFor={`${name}-select`}>
          {label}
          {requiredFields.includes(name) && (
            <span className="kyb-required">*</span>
          )}
        </label>
        <Select
          inputId={`${name}-select`}
          options={fieldOptions}
          className={`react-select-container ${validationErrors[name] ? "is-invalid" : ""}`}
          classNamePrefix="react-select"
          placeholder={config.placeholder || "Select"}
          isDisabled={config.disabled}
          isSearchable
          isClearable={!requiredFields.includes(name)}
          value={selectedOption || null}
          onChange={(selected) => handleSelectChange(name, selected?.value || "", selected)}
          menuPortalTarget={menuPortalTarget}
          menuPosition="fixed"
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
          }}
          noOptionsMessage={({ inputValue }) =>
            inputValue ? "No matching options" : "No options available"
          }
        />
        {validationErrors[name] ? (
          <small className="kyb-field-error">{validationErrors[name]}</small>
        ) : null}
      </div>
    );
  }

  function renderInputField(
    name,
    label,
    type = "text",
    config = { disabled: false, placeholder: "" }
  ) {
    return (
      <div className="kyb-field">
        <label className="kyb-field-label" htmlFor={name}>
          {label}
          {requiredFields.includes(name) && (
            <span className="kyb-required">*</span>
          )}
        </label>
        <input
          id={name}
          name={name}
          type={type}
          className={`kyb-control${validationErrors[name] ? " kyb-control-error" : ""}`}
          placeholder={config.placeholder || `Enter ${label}`}
          value={formData[name]}
          min={type === "number" && name === "familyAnnualIncome" ? "0" : undefined}
          onChange={(e) => {
            let nextValue = e.target.value;

            if (name === "familyAnnualIncome" && type === "number") {
              if (nextValue === "") {
                nextValue = "";
              } else {
                const numericValue = Number(nextValue);
                nextValue =
                  Number.isFinite(numericValue) && numericValue < 0
                    ? "0"
                    : nextValue.replace(/-/g, "");
              }
            }

            setFormData((c) => ({ ...c, [name]: nextValue }));
            if (validationErrors[name]) {
              setValidationErrors((current) => {
                const next = { ...current };
                delete next[name];
                return next;
              });
            }
          }}
          disabled={config.disabled}
        />
        {validationErrors[name] ? (
          <small className="kyb-field-error">{validationErrors[name]}</small>
        ) : null}
      </div>
    );
  }

  function renderCheckboxGroupField(
    name,
    label,
    fieldOptions,
    config = { disabled: false }
  ) {
    const selectedValues = Array.isArray(formData[name]) ? formData[name] : [];
    const normalizedOptions = Array.isArray(fieldOptions)
      ? fieldOptions.filter((option) => String(option?.value || "").trim() !== "")
      : [];

    return (
      <div className="kyb-field kyb-field--checkbox-group">
        {label && <label className="kyb-field-label">{label}</label> }
        <div className="kyb-checkbox-group">
          {normalizedOptions.map((option) => {
            const optionValue = String(option?.value || "");
            const isChecked = selectedValues.includes(optionValue);

            return (
              <label key={optionValue} className="kyb-checkbox-option">
                <input
                  type="checkbox"
                  className="kyb-checkbox-input"
                  checked={isChecked}
                  disabled={config.disabled}
                  onChange={() => {
                    setFormData((current) => {
                      const currentValues = Array.isArray(current[name]) ? current[name] : [];
                      const nextValues = currentValues.includes(optionValue)
                        ? currentValues.filter((value) => value !== optionValue)
                        : [...currentValues, optionValue];
                      const nextState = {
                        ...current,
                        [name]: nextValues,
                      };

                      if (name === "schemeType") {
                        if (!nextValues.includes("postMatric")) {
                          nextState.admissionThroughCET = "";
                          nextState.completed10thFromGovernmentVidyaniketan = "";
                          nextState.alpaBhuOrRegisteredLabour = "";
                          nextState.stateId = "";
                          nextState.districtId = "";
                          nextState.talukaId = "";
                          nextState.universityId = "";
                          nextState.institutionName = "";
                          nextState.universityName = "";
                          nextState.currentCourseName = "";
                          nextState.yearOfStudyOrCourseLevel = "";
                        }

                        if (!nextValues.includes("preMatric")) {
                          nextState.hazardousOrUncleanOccupation = "";
                          nextState.udiseCodeOfSchool = "";
                        }
                      }

                      return nextState;
                    });

                    if (validationErrors[name]) {
                      setValidationErrors((current) => {
                        const next = { ...current };
                        delete next[name];
                        return next;
                      });
                    }
                  }}
                />
                <span className="kyb-checkbox-box" aria-hidden="true" />
                <span className="kyb-checkbox-label">{option.label}</span>
              </label>
            );
          })}
        </div>
        {validationErrors[name] ? (
          <small className="kyb-field-error">{validationErrors[name]}</small>
        ) : null}
      </div>
    );
  }

  function renderIncomeSliderField(
    name,
    label,
    config = { disabled: false }
  ) {
    const rawValue = Number(formData[name] || 0);
    const safeValue = Number.isFinite(rawValue)
      ? Math.min(Math.max(rawValue, 0), FAMILY_INCOME_MAX)
      : 0;
    const sliderPercent = (safeValue / FAMILY_INCOME_MAX) * 100;
    const incomeMarks = [0, 200000, 400000, 600000, 800000, 1000000, FAMILY_INCOME_MAX_LABEL_VALUE];

    return (
      <div className="kyb-field">
        <label className="kyb-field-label" htmlFor={`${name}-slider`}>
          {label}
        </label>

        <div className="kyb-income-slider">
          <div
            className="kyb-income-slider-tooltip"
            style={{ left: `${sliderPercent}%` }}
          >
            {formatIncomeSliderLabel(safeValue)}
          </div>

          <input
            id={`${name}-slider`}
            type="range"
            min="0"
            max={String(FAMILY_INCOME_MAX)}
            step={String(FAMILY_INCOME_STEP)}
            value={safeValue}
            disabled={config.disabled}
            className="kyb-income-slider-input"
            onChange={(e) => {
              const nextValue = Math.max(0, Number(e.target.value) || 0);
              setFormData((current) => ({
                ...current,
                [name]: String(nextValue),
              }));

              if (validationErrors[name]) {
                setValidationErrors((current) => {
                  const next = { ...current };
                  delete next[name];
                  return next;
                });
              }
            }}
          />

          <div className="kyb-income-slider-scale">
            {incomeMarks.map((mark) => (
              <span key={mark} className="kyb-income-slider-mark">
                {formatIncomeSliderLabel(mark)}
              </span>
            ))}
          </div>
        </div>

        {validationErrors[name] ? (
          <small className="kyb-field-error">{validationErrors[name]}</small>
        ) : null}
      </div>
    );
  }

  function handleReset() {
    setFormData(initialForm);
    setOpenSection(["schemeDetails"]);
    setValidationErrors({});
    setResultsPage(1);
    setSchemeSearch("");
    setError("");
    setAppliedBySchemeId({});
    setAppliedLoadingBySchemeId({});
    mahaAutofillAppliedRef.current = {
      religion: false,
      casteCategory: false,
      caste: false,
      gender: false,
      disability: false,
      domicileOfMaharashtra: false,
      familyAnnualIncome: false,
      birthOrder: false,
      stateId: false,
    };
    aadhaarAutofillAppliedRef.current = {
      gender: false,
      domicileOfMaharashtra: false,
      stateId: false,
    };
    setDescriptionModal({
      show: false,
      title: "",
      description: "",
      department: "",
      schemeData: null,
    });
    setBenefitModal({
      show: false,
      schemeName: "",
      benefitsData: null,
      loading: false,
      error: "",
    });
    setApplyModal({
      show: false,
      schemeId: "",
      schemeName: "",
      message: "",
      variant: "info",
      canProceed: false,
      academicYears: [],
      selectedAcademicYear: "",
      loading: false,
      title: "",
      steps: [
        { id: 1, name: "Check previous applications", status: "pending", result: "" },
        { id: 2, name: "Validate eligibility rules", status: "pending", result: "" },
        { id: 3, name: "Final result", status: "pending", result: "" },
      ],
    });
  }

  const isInitialLoading = loading.initial;
  const isCasteLoading = loading.castes;

  const visibleSchemeTypeOptions = useMemo(
    () => (isHeadMasterUser ? headMasterSchemeTypeOptions : schemeTypeOptions),
    [isHeadMasterUser],
  );
  const effectiveFormData = useMemo(
    () =>
      isHeadMasterUser
        ? {
            ...formData,
            schemeType: ["preMatric"],
          }
        : formData,
    [formData, isHeadMasterUser],
  );
  const eligibilityRuleFormData = effectiveFormData;

  const hasMeaningfulFilters = useMemo(
    () => {
      const allowedInitialAutofillFields = new Set([
        "domicileOfMaharashtra",
        "gender",
        "stateId",
        "stateLabel",
      ]);

      return Object.entries(eligibilityRuleFormData).some(([key, value]) => {
        if (NON_MEANINGFUL_FILTER_KEYS.has(key)) {
          return false;
        }

        const normalizedArrayValue = Array.isArray(value)
          ? value.filter((item) => String(item ?? "").trim() !== "")
          : null;
        const hasValue = Array.isArray(value)
          ? normalizedArrayValue.length > 0
          : String(value ?? "").trim() !== "";

        if (!hasValue) {
          return false;
        }

        const isAutofilled =
          Boolean(mahaAutofillAppliedRef.current?.[key]) ||
          Boolean(aadhaarAutofillAppliedRef.current?.[key]);

        if (isAutofilled && !allowedInitialAutofillFields.has(key)) {
          return false;
        }

        return true;
      });
    },
    [eligibilityRuleFormData],
  );

  useEffect(() => {
    if (!isRoleResolved) {
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setLoading((current) => ({ ...current, results: true }));
        setError("");

        const schemes = hasMeaningfulFilters
          ? await checkEligibilityAgainstSchemeConfigurators(eligibilityRuleFormData, {
              skipCategoryMatches: isHeadMasterUser,
            })
          : await fetchBrowsableSchemeConfigurators(
              Array.isArray(effectiveFormData.schemeType) ? effectiveFormData.schemeType : [],
              { skipCategoryMatches: isHeadMasterUser },
            );

        if (cancelled) {
          return;
        }

        setEligibleSchemes(Array.isArray(schemes) ? schemes : []);
        setResultsPage(1);
        setAppliedBySchemeId({});
        setAppliedLoadingBySchemeId({});
        setAppliedStatusRefreshKey((current) => current + 1);
      } catch (e) {
        console.error("Live scheme filtering failed:", e);
        if (!cancelled) {
          setError(e?.message || "Failed to load schemes.");
          setEligibleSchemes([]);
          setResultsPage(1);
          setAppliedBySchemeId({});
          setAppliedLoadingBySchemeId({});
          setAppliedStatusRefreshKey((current) => current + 1);
        }
      } finally {
        if (!cancelled) {
          setLoading((current) => ({ ...current, results: false }));
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [effectiveFormData, eligibilityRuleFormData, hasMeaningfulFilters, isHeadMasterUser, isRoleResolved]);

  const filteredSchemes = useMemo(() => {
    const searchValue = String(schemeSearch || "").trim().toLowerCase();
    if (!searchValue) {
      return eligibleSchemes;
    }

    return eligibleSchemes.filter((scheme) => {
      const haystack = [
        scheme?.schemeName,
        scheme?.department,
        scheme?.description,
        scheme?.benefitAmount,
      ]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");

      return haystack.includes(searchValue);
    });
  }, [eligibleSchemes, schemeSearch]);

  const totalResultsPages = Math.max(1, Math.ceil(filteredSchemes.length / itemsPerPage));
  const safeResultsPage = Math.min(resultsPage, totalResultsPages);
  const paginationPageNumbers = useMemo(
    () => getPaginationPageNumbers(safeResultsPage, totalResultsPages),
    [safeResultsPage, totalResultsPages],
  );
  const startIndex = filteredSchemes.length === 0 ? 0 : (safeResultsPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const visibleSchemes = filteredSchemes.slice(startIndex, endIndex);
  const visibleSchemeIdsKey = visibleSchemes.map((s) => s.id).join(",");
  const benefitSubmissionModal = useMemo(
    () => ({
      ...benefitModal,
      title: "Benefits Details",
      variant: benefitModal.loading ? "info" : benefitModal.variant || "secondary",
      message: benefitModal.loading ? (
        <div className="py-3 text-center">
          {benefitModal.schemeName ? (
            <p className="text-muted small mb-2">{benefitModal.schemeName}</p>
          ) : null}
          <p className="mb-0">Loading scheme benefits...</p>
        </div>
      ) : benefitModal.schemeName ? (
        <p className="text-muted small mb-0">{benefitModal.schemeName}</p>
      ) : null,
      benefitsData: null,
      steps: null,
    }),
    [benefitModal],
  );

  useEffect(() => {
    setResultsPage(1);
  }, [itemsPerPage, schemeSearch]);

  useEffect(() => {
    if (!visibleSchemes.length) return;

    const loginUserId = Number(window?.Liferay?.ThemeDisplay?.getUserId?.() || 0);
    if (!loginUserId) return;

    let cancelled = false;
    (async () => {
      await Promise.all(
        visibleSchemes.map(async (scheme) => {
          const schemeId = String(scheme?.id || "");
          if (!schemeId) return;
          if (typeof appliedBySchemeId[schemeId] !== "undefined") return;
          if (appliedLoadingBySchemeId[schemeId]) return;

          const raw = scheme?.raw || null;
          const targetObjectDefinitionId = Number(
            raw?.targetObjectDefinitionId ||
              raw?.targetobjectdefinitionid ||
              raw?.targetObjectId ||
              0
          );
          if (!targetObjectDefinitionId) return;

          setAppliedLoadingBySchemeId((c) => ({ ...c, [schemeId]: true }));
          try {
            const res = await checkUserAlreadyAppliedForScheme(
              targetObjectDefinitionId,
              loginUserId
            );
            if (cancelled) return;
            const isApplied = Array.isArray(res?.items) && res.items.length > 0;
            setAppliedBySchemeId((c) => ({ ...c, [schemeId]: isApplied }));
          } catch (e) {
            console.error("Already applied check failed:", e);
          } finally {
            if (!cancelled) {
              setAppliedLoadingBySchemeId((c) => ({ ...c, [schemeId]: false }));
            }
          }
        })
      );
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSchemeIdsKey, appliedStatusRefreshKey]);


  const handleViewDescription = useCallback(async (scheme) => {
    const schemeId = String(scheme?.id || "").trim();

    setDescriptionModal({
      show: true,
      title: scheme?.schemeName || "",
      description: scheme?.description || "",
      department: resolveDepartmentDisplayName(scheme, departmentNameById),
      schemeData: null,
    });

    if (!schemeId) {
      setDescriptionModal((prev) => ({
        ...prev,
        schemeData: scheme || null,
      }));
      return;
    }

    const fullScheme = await fetchSchemeById(schemeId);

    const departmentDisplayName =
      String(fullScheme?.departmentName || fullScheme?.departmentLabel || "").trim() ||
      departmentNameById[String(fullScheme?.department || "").trim()] ||
      String(scheme?.departmentName || scheme?.departmentLabel || "").trim() ||
      departmentNameById[String(scheme?.department || "").trim()] ||
      String(fullScheme?.department || scheme?.department || "").trim() ||
      "";

    const hydratedScheme = {
      ...(fullScheme || {}),
      id: fullScheme?.id ?? scheme?.id,
      schemeName: fullScheme?.schemeName || scheme?.schemeName || "",
      department: departmentDisplayName,
      description: fullScheme?.description || scheme?.description || "",
    };

    setDescriptionModal((prev) => ({
      ...prev,
      title: hydratedScheme.schemeName,
      description: hydratedScheme.description,
      department: hydratedScheme.department,
      schemeData: hydratedScheme,
    }));
  }, [departmentNameById]);

  const handleTentativeBenefitsClick = useCallback(
    async (scheme) => {
      const schemeName = scheme?.schemeName || "";
      const schemeId = String(scheme?.id || "").trim();

      setBenefitModal({
        show: true,
        title: "Benefits Details",
        message: null,
        variant: "info",
        schemeName,
        benefitHtml: "",
        benefitsData: null,
        loading: true,
        benefitsError: "",
        benefitsErrorList: [],
      });

      try {
        let schemeForBenefits = scheme?.raw || scheme;
        let benefitHtml = getSchemeBenefitsHtmlContent(schemeForBenefits);

        if (!benefitHtml && schemeId) {
          const fullScheme = await fetchSchemeById(schemeId);
          if (fullScheme && typeof fullScheme === "object") {
            schemeForBenefits = { ...schemeForBenefits, ...fullScheme };
            benefitHtml = getSchemeBenefitsHtmlContent(schemeForBenefits);
          }
        }

        if (!benefitHtml) {
          setBenefitModal({
            show: true,
            title: "Benefits Details",
            message: null,
            variant: "danger",
            schemeName,
            benefitHtml: "",
            benefitsData: null,
            loading: false,
            benefitsError: "Scheme benefits details are not available for this scheme.",
            benefitsErrorList: [],
          });
          return;
        }

        setBenefitModal({
          show: true,
          title: "Benefits Details",
          message: null,
          variant: "secondary",
          schemeName,
          benefitHtml,
          benefitsData: null,
          loading: false,
          benefitsError: "",
          benefitsErrorList: [],
        });
      } catch (e) {
        console.error("Failed to load scheme benefits:", e);
        setBenefitModal({
          show: true,
          title: "Benefits Details",
          message: null,
          variant: "danger",
          schemeName,
          benefitHtml: "",
          benefitsData: null,
          loading: false,
          benefitsError: "Unable to load scheme benefits.",
          benefitsErrorList: [],
        });
      }
    },
    [],
  );

  const handleOpenGrDocuments = useCallback(async (scheme) => {
    const schemeId = String(scheme?.id || "").trim();

    setGrDownloadModal({
      show: true,
      schemeName: scheme?.schemeName || "",
      schemeGr: scheme?.grLink || scheme?.documents || "",
    });

    if (!schemeId) return;

    const fullScheme = await fetchSchemeById(schemeId);

    const schemeGrFromApi =
      fullScheme?.grDocument ??
      fullScheme?.grLink ??
      fullScheme?.documents ??
      fullScheme?.grDocuments ??
      "";

    setGrDownloadModal({
      show: true,
      schemeName: fullScheme?.schemeName || scheme?.schemeName || "",
      schemeGr: schemeGrFromApi,
    });
  }, []);

  async function handleApplyClick(scheme) {
    const schemeId = String(scheme?.id || "");
    if (!schemeId) return;

    const loginUserId = Number(
      window?.Liferay?.ThemeDisplay?.getUserId?.() || 0
    );

    const baseSteps = [
      { id: 1, name: "Check previous applications", status: "processing", result: "" },
      { id: 2, name: "Validate eligibility rules", status: "pending", result: "" },
      { id: 3, name: "Final result", status: "pending", result: "" },
    ];

    setApplyModal({
      show: true,
      schemeId,
      schemeName: scheme?.schemeName || "",
      title: "Checking previously applied",
      message: `Checking if you've previously applied for this scheme...`,
      variant: "info",
      canProceed: false,
      academicYears: [],
      selectedAcademicYear: "",
      loading: true,
      steps: baseSteps,
    });

    try {
      const schemeData = await fetchSchemesBySchemeId(schemeId);
      const alreadyAppliedRes =
        schemeData && loginUserId
          ? await checkUserAlreadyApplied(schemeData, loginUserId)
          : { items: [] };

      const hasApplied =
        Array.isArray(alreadyAppliedRes?.items) &&
        alreadyAppliedRes.items.length > 0;

      if (hasApplied) {
        setApplyModal((m) => ({
          ...m,
          loading: false,
          canProceed: false,
          title: "Already Applied",
          variant: "primary",
          message: `You have already applied for "${scheme?.schemeName || "this scheme"}"\n\nYou cannot apply again.`,
          steps: [
            { id: 1, name: "Check previous applications", status: "completed", result: "Already Applied " },
            { id: 2, name: "Validate eligibility rules", status: "skipped", result: "N/A" },
            { id: 3, name: "Final result", status: "skipped", result: "N/A" },
          ],
        }));
        return;
      }

      setApplyModal((m) => ({
        ...m,
        title: "Checking Eligibility",
        steps: [
          { id: 1, name: "Check previous applications", status: "completed", result: "No Applications" },
          { id: 2, name: "Validate eligibility rules", status: "processing", result: "" },
          { id: 3, name: "Final result", status: "pending", result: "" },
        ],
        message: `✓ Not applied before. Now validating eligibility rules...`,
        loading: true,
      }));

      const eligibilityRes = await validateSchemeEligibilityFromSchemeData(
        schemeData || scheme?.raw || scheme,
        formData
      );

      if (!eligibilityRes?.ok) {
        setApplyModal((m) => ({
          ...m,
          loading: false,
          canProceed: false,
          title: "Error",
          variant: "danger",
          message: "Failed to validate eligibility rules for this scheme.",
          steps: [
            { id: 1, name: "Check previous applications", status: "completed", result: "No Applications" },
            { id: 2, name: "Validate eligibility rules", status: "error", result: "Failed" },
            { id: 3, name: "Final result", status: "error", result: "Not Eligible" },
          ],
        }));
        return;
      }

      const eligible = !!eligibilityRes?.eligible;

      if (eligible) {
        const academicYears = await fetchAcademicYearOptions();
        setApplyModal((m) => ({
          ...m,
          loading: false,
          canProceed: true,
          academicYears,
          selectedAcademicYear: "",
          title: "Eligible!",
          variant: "primary",
          message:
            "Congratulations! You have met all the eligibility criteria for FY.\n\nYou are now permitted to proceed with the application.",
          steps: [
            { id: 1, name: "Check previous applications", status: "completed", result: "No Applications" },
            { id: 2, name: "Validate eligibility rules", status: "completed", result: "Passed" },
            { id: 3, name: "Final result", status: "completed", result: "Eligible" },
          ],
        }));
      } else {
        setApplyModal((m) => ({
          ...m,
          loading: false,
          canProceed: false,
          title: "Not Eligible",
          variant: "danger",
          message:
            "Unfortunately, you don't meet the eligibility criteria.\n\nPlease check the scheme requirements and try again.",
          steps: [
            { id: 1, name: "Check previous applications", status: "completed", result: "No Applications" },
            { id: 2, name: "Validate eligibility rules", status: "completed", result: "Failed" },
            { id: 3, name: "Final result", status: "completed", result: "Not Eligible" },
          ],
        }));
      }
    } catch (e) {
      console.error("handleApplyClick failed:", e);
      setApplyModal((m) => ({
        ...m,
        loading: false,
        canProceed: false,
        title: "Error",
        variant: "danger",
        message: "Failed to check eligibility. Please try again later.",
        steps: [
          { id: 1, name: "Check previous applications", status: "error", result: "" },
          { id: 2, name: "Validate eligibility rules", status: "error", result: "" },
          { id: 3, name: "Final result", status: "error", result: "" },
        ],
      }));
    }
  }

  const handleProceedApply = () => {
    const schemeId = String(applyModal?.schemeId || "");
    const academicYear = String(applyModal?.selectedAcademicYear || "");
    if (!schemeId) return;
    if (!academicYear) return;

    const allSchemesPath = process.env.REACT_APP_ALL_SCHEMES_PATH || "/scheme-apply";
    const redirectUrl = `${window.location.origin}${allSchemesPath}#/${schemeId}?academicYear=${encodeURIComponent(
      academicYear
    )}`;
    window.location.href = redirectUrl;
  };

  return (
    <div className="kyb-page">
      <div className="kyb-shell">
        <div className="kyb-hero">
          <h1>Find Eligible Scheme / Know Your Benefits</h1>
        </div>

        <div className="kye-discovery-layout">
          <aside className="kye-sidebar">
            <div className="kye-sidebar-header">
              <div className="kye-sidebar-title">
                <SlidersHorizontal size={18} />
                <span>Filter By</span>
              </div>
              <button
                type="button"
                className="kye-reset-link"
                onClick={handleReset}
              >
                Reset Filters
              </button>
            </div>

            <div className="kye-sidebar-body">
              <FilterAccordionSection
                title="Who You Are"
                sectionKey="schemeDetails"
                openSection={openSection}
                onToggle={handleSectionToggle}
              >
                <div className="kyb-field-grid kyb-field-grid--single">
                  {renderCheckboxGroupField(
                    "schemeType",
                    "",
                    visibleSchemeTypeOptions,
                    { disabled: isInitialLoading }
                  )}
                </div>
              </FilterAccordionSection>

              <FilterAccordionSection
                title="Personal Details"
                sectionKey="personalDetails"
                openSection={openSection}
                onToggle={handleSectionToggle}
              >
                <div className="kyb-field-grid kyb-field-grid--single">
                  {renderSelectField(
                    "domicileOfMaharashtra",
                    "Whether you are a Domicile of Maharashtra?",
                    yesNoOptions,
                    { disabled: isInitialLoading, placeholder: "Select" }
                  )}
                  {renderSelectField(
                    "religion",
                    "Religion",
                    options.religions,
                    { disabled: isInitialLoading, placeholder: "Select" }
                  )}
                  {renderSelectField(
                    "casteCategory",
                    "Caste category",
                    sortedCasteCategoryOptions,
                    { disabled: isInitialLoading, placeholder: "Select" }
                  )}
                  {renderSelectField(
                    "beneficiaryCategory",
                    "Beneficiary Category",
                    options.beneficiaryCategories,
                    { disabled: isInitialLoading, placeholder: "Select" }
                  )}
                  {shouldRenderCasteField
                    ? renderSelectField("caste", "Caste", options.castes, {
                        disabled:
                          isInitialLoading ||
                          isCasteLoading ||
                          !formData.casteCategory,
                        placeholder: isCasteLoading
                          ? "Loading castes..."
                          : formData.casteCategory
                            ? "Select caste"
                            : "Select caste category first",
                      })
                    : null}
                  {renderSelectField(
                    "gender",
                    "Gender",
                    options.genders,
                    { disabled: isInitialLoading, placeholder: "Select" }
                  )}
                  {renderSelectField(
                    "disability",
                    "Do you have any type of disability?",
                    yesNoOptions,
                    { disabled: isInitialLoading, placeholder: "Select" }
                  )}
                </div>
              </FilterAccordionSection>

              <FilterAccordionSection
                title="Family Details"
                sectionKey="familyDetails"
                openSection={openSection}
                onToggle={handleSectionToggle}
              >
                <div className="kyb-field-grid kyb-field-grid--single">
                  {renderIncomeSliderField("familyAnnualIncome", "Family Annual Income", {
                    disabled: isInitialLoading,
                  })}
                  {formData.schemeType.includes("postMatric")
                    ? renderSelectField(
                        "alpaBhuOrRegisteredLabour",
                        "Whether either parent is a Alpa Bhu-Dharak Shetkari / Registered labour?",
                        yesNoOptions,
                        { disabled: isInitialLoading, placeholder: "Select" }
                      )
                    : null}
                  {formData.schemeType.includes("preMatric")
                    ? renderSelectField(
                        "hazardousOrUncleanOccupation",
                        "Whether either parent undertakes hazardous / unclean occupation?",
                        yesNoOptions,
                        { disabled: isInitialLoading, placeholder: "Select" }
                      )
                    : null}
                  {renderSelectField(
                    "birthOrder",
                    "Applicant’s birth order within the family",
                    options.birthOrders,
                    {
                      disabled: isInitialLoading,
                      placeholder: options.birthOrders?.length
                        ? "Select"
                        : "No options available",
                    }
                  )}
                </div>
              </FilterAccordionSection>

              {formData.schemeType.includes("postMatric") ? (
                <CourseDetailsFields
                  openSection={openSection}
                  onToggle={handleSectionToggle}
                  isInitialLoading={isInitialLoading}
                  loading={loading}
                  formData={formData}
                  options={options}
                  validationErrors={validationErrors}
                  menuPortalTarget={menuPortalTarget}
                  setInstitutionSearch={setInstitutionSearch}
                  setFormData={setFormData}
                  handleInstitutionSelect={handleInstitutionSelect}
                  renderSelectField={renderSelectField}
                  isCourseTypeAutoFilled={isCourseTypeAutoFilled}
                />
              ) : null}

              {formData.schemeType.includes("preMatric") ? (
                <GradeAndSchoolDetailsFields
                  openSection={openSection}
                  onToggle={handleSectionToggle}
                  isInitialLoading={isInitialLoading}
                  renderSelectField={renderSelectField}
                  renderInputField={renderInputField}
                />
              ) : null}
            </div>
          </aside>

          <section className="kye-results-panel">
            <div className="kye-results-toolbar">
              <div className="kye-search-box">
                <Search size={18} />
                <input
                  type="text"
                  className="kye-search-input"
                  placeholder="Search schemes"
                  value={schemeSearch}
                  onChange={(e) => setSchemeSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="kye-results-summary">
              <div className="kye-results-count">
                {loading.results
                  ? "Updating schemes..."
                  : `We found ${filteredSchemes.length} scheme${filteredSchemes.length === 1 ? "" : "s"} based on your filters`}
              </div>
              {error ? <div className="kye-results-error">{error}</div> : null}
            </div>

            <div className="kye-results-list">
              {!loading.results && filteredSchemes.length === 0 ? (
                <div className="kye-empty-state">
                  No schemes matched the selected filters. Try clearing some filters to see more results.
                </div>
              ) : null}

              {visibleSchemes.map((scheme) => {
                const schemeId = String(scheme?.id || "");
                const isApplied = appliedBySchemeId[schemeId] === true;
                const isChecking = appliedLoadingBySchemeId[schemeId] === true;
                const deptName = resolveDepartmentDisplayName(scheme, departmentNameById);

                return (
                  <article className="kye-scheme-card" key={schemeId}>
                    <div className="kye-scheme-card-body">
                      <div className="kye-scheme-card-header">
                        <div>
                          <h3 className="kye-scheme-title">{scheme?.schemeName || "-"}</h3>
                          <p className="kye-scheme-department">{deptName}</p>
                        </div>
                      </div>

                      <p className="kye-scheme-description">
                        {scheme?.description || "No description available."}
                      </p>

                      <div className="kye-scheme-actions">
                        <button
                          type="button"
                          className="btn kyb-icon-btn"
                          onClick={() =>
                            handleViewDescription({
                              ...scheme,
                              department: deptName,
                            })
                          }
                          title="View full description"
                        >
                          <Eye size={14} />
                          <span>Details</span>
                        </button>

                        <button
                          type="button"
                          className="btn kyb-icon-btn"
                          onClick={() => handleOpenGrDocuments(scheme)}
                          title="View GR documents"
                        >
                          <Eye size={14} />
                          <span>GRs</span>
                        </button>

                        <button
                          type="button"
                          className="btn kyb-brand-btn kyb-apply-btn kyb-apply-btn-text"
                          onClick={() =>
                            handleTentativeBenefitsClick({
                              ...scheme,
                              department: deptName,
                            })
                          }
                        >
                          <span className="kye-scheme-benefit-label">Tentative Benefit</span>
                        </button>

                        {!isGuestUser ? (
                          <button
                            type="button"
                            className="btn kyb-brand-btn kyb-apply-btn"
                            disabled={isApplied || isChecking}
                            onClick={() => handleApplyClick(scheme)}
                          >
                            <ExternalLink size={14} />
                            <span className="kyb-apply-btn-text">
                              {isChecking ? "Checking..." : isApplied ? "Applied" : "Apply"}
                            </span>
                          </button>
                        ) : null}
                        
                         
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="kyb-pagination-bar">
              <div className="kyb-rows-per-page">
                <span className="kyb-rows-per-page-label">Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="kyb-page-size-select"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              {totalResultsPages > 1 ? (
                <div className="kyb-pagination-buttons">
                  <button
                    type="button"
                    className="kyb-page-btn kyb-page-btn--outline"
                    disabled={safeResultsPage === 1}
                    onClick={() => setResultsPage((page) => Math.max(1, page - 1))}
                  >
                    Previous
                  </button>

                  <div className="kyb-page-number-row">
                    {paginationPageNumbers.map((page) =>
                      typeof page === "number" ? (
                        <button
                          key={page}
                          type="button"
                          className={`kyb-page-number ${page === safeResultsPage ? "kyb-page-number--active" : ""}`}
                          onClick={() => setResultsPage(page)}
                        >
                          {page}
                        </button>
                      ) : (
                        <span key={page} className="kyb-page-ellipsis" aria-hidden="true">
                          &hellip;
                        </span>
                      )
                    )}
                  </div>

                  <button
                    type="button"
                    className="kyb-page-btn kyb-page-btn--outline"
                    disabled={safeResultsPage === totalResultsPages}
                    onClick={() =>
                      setResultsPage((page) => Math.min(totalResultsPages, page + 1))
                    }
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        </div>

        {descriptionModal.show ? (
          <SchemeAndGRDetailsModal
            scheme={{
              ...(descriptionModal.schemeData || {}),
              schemeName: descriptionModal.schemeData?.schemeName || descriptionModal.title || "",
              department: descriptionModal.schemeData?.department || descriptionModal.department || "",
              description: descriptionModal.schemeData?.description || descriptionModal.description || "",
              grDocument:
                descriptionModal.schemeData?.grDocument ??
                descriptionModal.schemeData?.grLink ??
                descriptionModal.schemeData?.documents ??
                "",
            }}
            onClose={() =>
              setDescriptionModal({
                show: false,
                title: "",
                description: "",
                department: "",
                schemeData: null,
              })
            }
            showFaq={true}
          />
        ) : null}

        {benefitModal.show ? (
          <ApplicationSubmissionModal
            submissionModal={benefitSubmissionModal}
            setSubmissionModal={setBenefitModal}
            scrollToFirstErrorField={() => {}}
            navigate={() => {}}
          />
        ) : null}

        {applyModal.show ? (
          <div className="kyb-modal-overlay" role="dialog" aria-modal="true">
            <div className="kyb-modal">
              <div className="kyb-modal-header">
                <div className="kyb-modal-title">
                  {(applyModal.title || "").toLowerCase().includes("checking") ? (
                    <span className="kyb-header-spinner" role="status" aria-label="Loading" />
                  ) : null}
                  {applyModal.title || "Apply"}
                </div>
                <button
                  type="button"
                  className="kyb-modal-close"
                  onClick={() =>
                    setApplyModal({
                      show: false,
                      schemeId: "",
                      schemeName: "",
                      title: "",
                      message: "",
                      variant: "info",
                      canProceed: false,
                      academicYears: [],
                      selectedAcademicYear: "",
                      loading: false,
                      steps: [
                        { id: 1, name: "Check previous applications", status: "pending", result: "" },
                        { id: 2, name: "Validate eligibility rules", status: "pending", result: "" },
                        { id: 3, name: "Final result", status: "pending", result: "" },
                      ],
                    })
                  }
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="kyb-modal-body">
                <div className="kyb-apply-stepper">
                  {(() => {
                    const lastNonFinal = applyModal.steps[applyModal.steps.length - 2];
                    const status = lastNonFinal?.status;
                    const bg =
                      status === "completed"
                        ? "#0f3b82"
                        : status === "skipped"
                          ? "#6c757d"
                          : "#dee2e6";

                    return (
                      <div
                        className="kyb-apply-stepper-line"
                        aria-hidden="true"
                        style={{ backgroundColor: bg }}
                      />
                    );
                  })()}
                  <div className="kyb-apply-stepper-row">
                    {applyModal.steps.map((step, idx) => {
                      const isCompleted = step.status === "completed";
                      const isError = step.status === "error";
                      const isSkipped = step.status === "skipped";
                      const isProcessing = step.status === "processing";
                      const isPending = step.status === "pending";
                      const trimmedResult = String(step.result || "").trim();

                      const badgeVariant =
                        trimmedResult === "Passed" || trimmedResult === "Eligible"
                          ? "success"
                          : trimmedResult === "Failed" || trimmedResult === "Not Eligible"
                            ? "danger"
                            : trimmedResult === "Applied"
                              ? "warning"
                              : "neutral";

                      return (
                        <div className="kyb-apply-step" key={step.id || idx}>
                          <div className={`kyb-apply-step-circle kyb-apply-step-circle--${step.status}`}>
                            {isCompleted
                              ? "✓"
                              : isError
                                ? "✗"
                                : isSkipped
                                  ? "–"
                                  : isProcessing
                                    ? <span className="kyb-step-spinner" role="status" aria-label="Loading" />
                                    : isPending
                                      ? step.id
                                      : step.id}
                          </div>
                          <div className="kyb-apply-step-name">{step.name}</div>
                          {trimmedResult && trimmedResult !== "N/A" ? (
                            <div className={`kyb-apply-step-badge kyb-apply-step-badge--${badgeVariant}`}>
                              {trimmedResult}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="kyb-apply-message-card">
                  <div className="kyb-apply-message-text">{applyModal.message}</div>

                  {applyModal.canProceed ? (
                    <>
                      <div className="kyb-apply-dropdown-title">
                        Select Academic Year <span className="kyb-required">*</span>
                      </div>
                      <select
                        className="kyb-control"
                        value={applyModal.selectedAcademicYear}
                        onChange={(e) =>
                          setApplyModal((modal) => ({
                            ...modal,
                            selectedAcademicYear: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select Academic Year</option>
                        {applyModal.academicYears.map((year) => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                      <div className="kyb-micro-note">
                        Please select the academic year for which you want to apply.
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
              <div className="kyb-modal-footer">
                <button
                  type="button"
                  className="btn kyb-brand-btn"
                  onClick={() =>
                    setApplyModal({
                      show: false,
                      schemeId: "",
                      schemeName: "",
                      title: "",
                      message: "",
                      variant: "info",
                      canProceed: false,
                      academicYears: [],
                      selectedAcademicYear: "",
                      loading: false,
                      steps: [
                        { id: 1, name: "Check previous applications", status: "pending", result: "" },
                        { id: 2, name: "Validate eligibility rules", status: "pending", result: "" },
                        { id: 3, name: "Final result", status: "pending", result: "" },
                      ],
                    })
                  }
                >
                  Close
                </button>

                {applyModal.canProceed &&
                applyModal.selectedAcademicYear &&
                !applyModal.loading ? (
                  <button
                    type="button"
                    className="btn kyb-brand-btn"
                    onClick={handleProceedApply}
                  >
                    Proceed to Apply
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        <GRDownloadModal
          GRDownloadVisible={grDownloadModal}
          setGRDownloadVisible={setGrDownloadModal}
        />
      </div>
    </div>
  );
}

export default KnowYourEligibilityForm;
